import React from 'react';
import { Modal } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';
import { Changes as ChangesApi } from '../../services';
import { Project } from '../../models';
import { linkifyChangeSlugs } from '../../utils/linkifyChangeSlugs';
import { ensureExternalLinksOpenBlank } from '../../utils/ensureExternalLinksOpenBlank';
import { ensureRenderableHtml } from '../../utils/ensureRenderableHtml';
import { hydrateRemoteImages } from '../../utils/remoteImages';
import { useRemoteImages } from '../RemoteImages';
import { Icon } from '../Icon';

interface HtmlContentProps {
    html: string;
    className?: string;
    style?: React.CSSProperties;
    projectId?: string;
}

/**
 * Render HTML with auto-linked change slugs (F/B/C/S + number; T legacy).
 * Clicking a slug resolves it via API and navigates to the card.
 * Clicking an inline image opens the same lightbox preview as attachments.
 * Other links open in a new tab.
 * Remote intake images (`data-remote-img`) hydrate only when the card session allows it.
 */
const HtmlContent = (props: HtmlContentProps) => {
    const { html, className, style, projectId } = props;
    const navigate = useNavigate();
    const [pending, setPending] = React.useState(false);
    const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);
    const [previewUrls, setPreviewUrls] = React.useState<string[]>([]);
    const { showRemoteImages } = useRemoteImages();

    const renderedHtml = React.useMemo(() => {
        const withBreaks = ensureRenderableHtml(html || '');
        const withLinks = ensureExternalLinksOpenBlank(linkifyChangeSlugs(withBreaks));
        return showRemoteImages ? hydrateRemoteImages(withLinks) : withLinks;
    }, [html, showRemoteImages]);

    React.useEffect(() => {
        if (previewIndex === null)
            return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                // Capture + stop so ChangeModal Escape does not close the card.
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                setPreviewIndex(null);
                return;
            }
            if (previewUrls.length < 2)
                return;
            if (e.key === 'ArrowRight')
                setPreviewIndex(i => (i === null ? i : (i + 1) % previewUrls.length));
            if (e.key === 'ArrowLeft')
                setPreviewIndex(i => (i === null ? i : (i - 1 + previewUrls.length) % previewUrls.length));
        };

        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [previewIndex, previewUrls.length]);

    const stepPreview = (delta: number) => {
        setPreviewIndex(i => (
            i === null ? i : (i + delta + previewUrls.length) % previewUrls.length
        ));
    };

    const openImagePreview = (event: React.MouseEvent<HTMLElement>, img: HTMLImageElement) => {
        const container = event.currentTarget;
        const images = Array.from(container.querySelectorAll('img'));
        const urls = images.map((node) => node.currentSrc || node.src).filter(Boolean);
        const index = images.indexOf(img);
        if (index < 0 || !urls.length)
            return;

        event.preventDefault();
        event.stopPropagation();
        setPreviewUrls(urls);
        setPreviewIndex(index);
    };

    const onClick = async (event: React.MouseEvent<HTMLElement>) => {
        // Left-click only — middle-click is handled by onAuxClickCapture.
        if (event.button !== 0)
            return;

        const target = event.target as HTMLElement | null;
        const img = target?.closest?.('img') as HTMLImageElement | null;
        if (img) {
            openImagePreview(event, img);
            return;
        }

        const anchor = target?.closest?.('a[data-yoda-change-slug]') as HTMLAnchorElement | null;
        if (!anchor)
            return;

        event.preventDefault();
        event.stopPropagation();

        const slug = anchor.getAttribute('data-yoda-change-slug');
        if (!slug || pending)
            return;

        setPending(true);
        try {
            const { change } = await ChangesApi.getBySlug(slug, projectId);
            navigate(Project.path(change.project, 'change', change._id));
        } catch (err) {
            console.error(err);
        } finally {
            setPending(false);
        }
    };

    const onAuxClick = async (event: React.MouseEvent<HTMLElement>) => {
        if (event.button !== 1)
            return;

        const target = event.target as HTMLElement | null;
        const anchor = target?.closest?.('a[data-yoda-change-slug]') as HTMLAnchorElement | null;
        if (!anchor)
            return;

        event.preventDefault();
        event.stopPropagation();

        const slug = anchor.getAttribute('data-yoda-change-slug');
        if (!slug || pending)
            return;

        setPending(true);
        try {
            const { change } = await ChangesApi.getBySlug(slug, projectId);
            const path = Project.path(change.project, 'change', change._id);
            window.open(path, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error(err);
        } finally {
            setPending(false);
        }
    };

    const mergedClassName = [
        className,
        'min-w-0 max-w-full break-words',
        '[&_img]:max-h-80 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:border [&_img]:cursor-zoom-in',
        // Marketing tables often keep HTML width attrs; force them into the container.
        '[&_table]:!max-w-full [&_table]:!w-auto [&_table]:border-collapse',
        '[&_td]:align-top [&_td]:break-words [&_th]:align-top [&_th]:break-words',
    ].filter(Boolean).join(' ');

    return (
        <>
            <div
                className={mergedClassName}
                style={style}
                onClickCapture={onClick}
                onMouseDownCapture={(event) => {
                    if (event.button === 1 && (event.target as HTMLElement | null)?.closest?.('a[data-yoda-change-slug]'))
                        event.preventDefault();
                }}
                onAuxClickCapture={onAuxClick}
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
            <Modal show={previewIndex !== null} onClose={() => setPreviewIndex(null)} size="7xl" popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="relative flex items-center justify-center min-h-[40vh] px-8">
                        {previewUrls.length > 1 && (
                            <button
                                type="button"
                                className="absolute left-0 p-2 text-gray-400 hover:text-gray-700 dark:text-gray-200"
                                onClick={() => stepPreview(-1)}
                            >
                                <Icon type="left" size={8} />
                            </button>
                        )}
                        {previewIndex !== null && previewUrls[previewIndex] && (
                            <img
                                src={previewUrls[previewIndex]}
                                alt=""
                                className="max-h-[85vh] w-auto max-w-full object-contain mx-auto"
                            />
                        )}
                        {previewUrls.length > 1 && (
                            <button
                                type="button"
                                className="absolute right-0 p-2 text-gray-400 hover:text-gray-700 dark:text-gray-200"
                                onClick={() => stepPreview(1)}
                            >
                                <Icon type="right" size={8} />
                            </button>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default HtmlContent;
